import React, { useState, useEffect } from 'react';
import { Row, Col, Form, ListGroup, Button, Image } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { Eye, EyeSlash, XCircle } from 'react-bootstrap-icons';
import { useFileUpload } from 'hooks/useFileUpload';

/* EditableField Component remains unchanged */
const EditableField = ({
  label,
  name,
  value,
  error,
  onChange,
  onSave,
  originalValue,
  type = 'text',
  options,
  isSaving,
  isEditMode,
  disabled,
  placeholder,
  appendContent
}) => {
  const selectPlaceholder = placeholder || 'Select...';
  const [isEditing, setIsEditing] = useState(false);
  const [internalValue, setInternalValue] = useState(value || '');

  useEffect(() => setInternalValue(value || ''), [value]);

  const toggleEdit = async () => {
    if (isEditing) {
      if (internalValue === (originalValue || '')) {
        toast.info('No changes detected');
        setIsEditing(false);
        return;
      }
      try {
        await onSave(name, internalValue);
        setIsEditing(false);
      } catch (err) {
        toast.error(`Failed to update ${label}: ${err.message}`);
      }
    } else {
      setIsEditing(true);
    }
  };

  const renderOptions = () =>
    options?.map((opt) => {
      if (typeof opt === 'string') {
        return <option key={opt} value={opt}>{opt}</option>;
      } else if (typeof opt === 'object' && opt !== null) {
        return <option key={opt.value} value={opt.value}>{opt.label}</option>;
      }
      return null;
    });

  return (
    <Form.Group controlId={name} className="mb-3 position-relative">
      {isEditMode && onSave && (
        <div className="d-flex justify-content-between align-items-center mb-1">
          <Form.Label className="mb-0">{label}</Form.Label>
          <span
            role="button"
            tabIndex={0}
            onClick={toggleEdit}
            style={{ cursor: isSaving ? 'wait' : 'pointer', color: '#0d6efd' }}
          >
            {isSaving ? 'Saving...' : isEditing ? 'Save' : 'Edit'}
          </span>
        </div>
      )}

      {!isEditMode || !onSave ? (
        <>
          <Form.Label>{label}</Form.Label>
          {type === 'select' ? (
            <Form.Select name={name} value={value || ''} disabled={disabled} onChange={onChange}>
              <option value="">{selectPlaceholder}</option>
              {renderOptions()}
            </Form.Select>
          ) : (
            <div className="position-relative">
              <Form.Control
                name={name}
                value={value || ''}
                type={type}
                disabled={disabled}
                onChange={onChange}
                placeholder={placeholder}
              />
              {appendContent}
            </div>
          )}
        </>
      ) : (
        <>
          {type === 'select' ? (
            <Form.Select
              value={internalValue}
              disabled={!isEditing}
              onChange={(e) => setInternalValue(e.target.value)}
            >
              <option value="">{selectPlaceholder}</option>
              {renderOptions()}
            </Form.Select>
          ) : (
            <Form.Control
              value={internalValue}
              disabled={!isEditing}
              onChange={(e) => setInternalValue(e.target.value)}
            />
          )}
          {appendContent}
        </>
      )}

      <Form.Control.Feedback type="invalid">{error}</Form.Control.Feedback>
    </Form.Group>
  );
};

/* DocumentUploadSection – minor updates for clarity */
const DocumentUploadSection = ({
  label,
  existingDocuments = [],
  onUpload,
  onRemove,
  isEditMode,
  target,
  options = [],
}) => {
  const [documentType, setDocumentType] = useState('');
  const [frontFile, setFrontFile] = useState(null);
  const [backFile, setBackFile] = useState(null);

  const frontPreview = frontFile ? URL.createObjectURL(frontFile) : null;
  const backPreview = backFile ? URL.createObjectURL(backFile) : null;

  useEffect(() => {
    return () => {
      if (frontPreview) URL.revokeObjectURL(frontPreview);
      if (backPreview) URL.revokeObjectURL(backPreview);
    };
  }, [frontPreview, backPreview]);

  const handleSubmit = () => {
    if (!documentType) return toast.error('Document type is required');
    if (!frontFile || !backFile) return toast.error('Front and back files are required');

    onUpload([frontFile, backFile], documentType, target);

    setDocumentType('');
    setFrontFile(null);
    setBackFile(null);
  };

  const renderPreview = (file, labelText) => {
    if (!file) return <div className="text-muted small text-center">No {labelText}</div>;

    if (file.type === 'application/pdf') {
      return <div className="small text-muted">{file.name}</div>;
    }

    return (
      <Image
        src={URL.createObjectURL(file)}
        thumbnail
        className="w-100"
        style={{ maxHeight: 140, objectFit: 'contain' }}
      />
    );
  };

  return (
    <div className="mb-5">
      <h6 className="fw-semibold mb-3">{label}</h6>

      {existingDocuments.length > 0 && (
        <ListGroup className="mb-4">
          {existingDocuments.map((doc) => (
            <ListGroup.Item
              key={doc.id || doc.originalName}
              className="d-flex justify-content-between align-items-center"
            >
              <div>
                <div className="fw-medium">{doc.originalName}</div>
                <small className="text-muted">
                  {doc.documentType} • {doc.side}
                </small>
              </div>
              {isEditMode && (
                <XCircle
                  size={18}
                  className="text-danger cursor-pointer"
                  onClick={() => onRemove?.(doc.id, target)}
                />
              )}
            </ListGroup.Item>
          ))}
        </ListGroup>
      )}

      <Form.Group className="mb-3">
        <Form.Label>Document Type</Form.Label>
        <Form.Select
          value={documentType}
          onChange={(e) => setDocumentType(e.target.value)}
        >
          <option value="">Select document type</option>
          {options.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </Form.Select>
      </Form.Group>

      <Row className="g-3 mb-3">
        <Col md={6}>
          <input
            type="file"
            accept="image/*,application/pdf"
            hidden
            id={`${target}-front`}
            onChange={(e) => setFrontFile(e.target.files?.[0] ?? null)}
          />
          <Button
            variant="outline-primary"
            className="w-100"
            onClick={() => document.getElementById(`${target}-front`)?.click()}
          >
            Upload Front
          </Button>
          <div className="mt-2">{renderPreview(frontFile, 'front')}</div>
        </Col>

        <Col md={6}>
          <input
            type="file"
            accept="image/*,application/pdf"
            hidden
            id={`${target}-back`}
            onChange={(e) => setBackFile(e.target.files?.[0] ?? null)}
          />
          <Button
            variant="outline-primary"
            className="w-100"
            onClick={() => document.getElementById(`${target}-back`)?.click()}
          >
            Upload Back
          </Button>
          <div className="mt-2">{renderPreview(backFile, 'back')}</div>
        </Col>
      </Row>

      <div className="text-end">
        <Button
          variant="primary"
          disabled={!documentType || !frontFile || !backFile}
          onClick={handleSubmit}
        >
          Submit Documents
        </Button>
      </div>
    </div>
  );
};

/* Main PersonalForm Component */
const PersonalForm = ({
  formData = {},
  errors = {},
  handleInputChange,
  userId,
  patchProfileProperties,
  refetch,
  isEditMode,
  // New props from WizardLayout / useAddUserForm
  onIdentityFilesSelected,
  onWorkAuthFilesSelected,
  onIdentityTypeChange,
  onWorkAuthTypeChange,
}) => {
  const [originalValues, setOriginalValues] = useState({});
  const [savingFields, setSavingFields] = useState({});
  const [showSsn, setShowSsn] = useState(false);

  const {
    uploadFile,
    uploading,
    error: uploadError,
    submitIdentityVerification,
    submitWorkAuthorization,
  } = useFileUpload();

  // Only used in edit mode to display already uploaded documents
  const [uploadedFiles, setUploadedFiles] = useState({
    identity: [],
    workAuth: [],
  });

  useEffect(() => {
    const keys = ['birthDate', 'gender', 'ssn', 'status'];
    const orig = {};
    keys.forEach((k) => (orig[k] = formData[k] || ''));
    setOriginalValues(orig);

    // In edit mode → show documents from backend
    if (userId && isEditMode) {
      const mapDocs = (docs = []) =>
        docs.map((doc) => ({
          id: doc.id,
          documentType: doc.documentType || 'UNKNOWN',
          originalName: doc.originalName || 'Unknown',
          side: doc.side || 'UNKNOWN',
          previewUrl: doc.previewUrl || null,
        }));

      setUploadedFiles({
        identity: mapDocs(formData.userIdentityVerification?.documents || []),
        workAuth: mapDocs(formData.userWorkAuthorization?.documents || []),
      });
    }
  }, [formData, userId, isEditMode]);

  const handleFieldSave = async (fieldName, value) => {
    if (!userId) return;
    setSavingFields((p) => ({ ...p, [fieldName]: true }));
    try {
      await patchProfileProperties(userId, [{ name: fieldName, value }]);
      handleInputChange({ target: { name: fieldName, value } });
      if (refetch) await refetch();
      toast.success(`${fieldName} updated`);
    } catch (err) {
      toast.error(err.message || 'Failed to save');
    } finally {
      setSavingFields((p) => {
        const next = { ...p };
        delete next[fieldName];
        return next;
      });
    }
  };

  const handleUpload = async (files, documentType, target) => {
    if (files.length !== 2) {
      return toast.error('Both front and back are required');
    }

    if (userId) {
      // ── EDIT MODE ── real upload
      try {
        toast.info('Uploading documents...');

        const uploadedFileIds = [];
        const newDocs = [];

        for (let i = 0; i < files.length; i++) {
          const file = files[i];
          const isImg = /\.(jpe?g|png|webp|gif|heic)$/i.test(file.name);
          const result = await uploadFile(userId, file, {
            purpose: 'USER_PROFILE',
            fileType: isImg ? 'PHOTO' : 'DOCUMENT',
          });

          const fileId = result.fileId;
          uploadedFileIds.push(fileId);

          newDocs.push({
            id: fileId,
            documentType,
            originalName: file.name,
            side: i === 0 ? 'FRONT' : 'BACK',
          });
        }

        setUploadedFiles((prev) => ({
          ...prev,
          [target]: [...prev[target], ...newDocs],
        }));

        if (target === 'identity') {
          await submitIdentityVerification(userId, documentType, uploadedFileIds);
        } else {
          await submitWorkAuthorization(userId, documentType, uploadedFileIds);
        }

        toast.success('Documents uploaded and submitted');
        if (refetch) await refetch();
      } catch (err) {
        console.error(err);
        toast.error(err.message || 'Upload failed');
      }
    } else {
      // ── CREATE MODE ── forward to parent
      if (target === 'identity') {
        onIdentityFilesSelected?.(files);
        onIdentityTypeChange?.(documentType);
        toast.info('Identity documents attached. They will upload when you click Create User.');
      } else if (target === 'workAuth') {
        onWorkAuthFilesSelected?.(files);
        onWorkAuthTypeChange?.(documentType);
        toast.info('Work authorization documents attached. They will upload when you click Create User.');
      }
    }
  };

  const handleRemove = (fileId, target) => {
    if (!userId) return; // no remove in create mode yet

    setUploadedFiles((prev) => ({
      ...prev,
      [target]: prev[target].filter((d) => d.id !== fileId),
    }));

    // TODO: call backend delete API if implemented
    toast.info('Document removed');
  };

  // ── Options ──
  const identityOptions = [
    { label: 'Passport', value: 'PASSPORT' },
    { label: "Driver's License", value: 'DRIVERS_LICENSE' },
    { label: 'School ID', value: 'SCHOOL_ID' },
    { label: 'Government ID', value: 'GOVERNMENT_ID' },
  ];

  const workOptions = [
    { label: 'Citizen', value: 'CITIZEN' },
    { label: 'Green Card', value: 'GREEN_CARD' },
    { label: 'H1B', value: 'H1B' },
    { label: 'CPT', value: 'CPT' },
    { label: 'OPT', value: 'OPT' },
    { label: 'Student', value: 'STUDENT' },
    { label: 'Visitor', value: 'VISITOR' },
    { label: 'Other', value: 'OTHER' },
  ];

  const genderOptions = [
    { label: 'Male', value: 'MALE' },
    { label: 'Female', value: 'FEMALE' },
    { label: 'Other', value: 'OTHER' },
  ];

  const statusOptions = [
    { label: 'Active', value: 'ACTIVE' },
    { label: 'Disabled', value: 'DISABLED' },
    { label: 'Suspended', value: 'SUSPENDED' },
    { label: 'Deleted', value: 'DELETED' },
  ];

  const ssnAppend = (
    <Button
      variant="link"
      onClick={() => setShowSsn((s) => !s)}
      className="position-absolute top-50 end-0 translate-middle-y text-muted p-2"
      tabIndex={-1}
    >
      {showSsn ? <EyeSlash size={18} /> : <Eye size={18} />}
    </Button>
  );

  return (
    <>
      {uploading && <p className="text-info">Uploading…</p>}
      {uploadError && <p className="text-danger">{uploadError}</p>}

      <Row className="g-4">
        {/* Date of Birth, Gender, SSN, Status fields – unchanged */}
        <Col md={6}>
          <EditableField
            label="Date of Birth"
            name="birthDate"
            type="date"
            value={formData.birthDate || ''}
            error={errors.birthDate}
            onChange={handleInputChange}
            onSave={isEditMode ? handleFieldSave : null}
            originalValue={originalValues.birthDate}
            isSaving={savingFields.birthDate}
            isEditMode={isEditMode}
            placeholder="YYYY-MM-DD"
          />
        </Col>
        <Col md={6}>
          <EditableField
            label="Gender"
            name="gender"
            type="select"
            options={genderOptions}
            value={formData.gender || ''}
            error={errors.gender}
            onChange={handleInputChange}
            onSave={isEditMode ? handleFieldSave : null}
            originalValue={originalValues.gender}
            isSaving={savingFields.gender}
            isEditMode={isEditMode}
          />
        </Col>
        <Col md={6}>
          <EditableField
            label="SSN"
            name="ssn"
            type={showSsn ? 'text' : 'password'}
            value={formData.ssn || ''}
            error={errors.ssn}
            onChange={handleInputChange}
            onSave={null}
            originalValue={originalValues.ssn}
            isSaving={savingFields.ssn}
            isEditMode={false}
            placeholder="123-45-6789"
            disabled={isEditMode}
            appendContent={ssnAppend}
          />
        </Col>
        <Col md={6}>
          <EditableField
            label="Status"
            name="status"
            type="select"
            options={statusOptions}
            value={formData.status || ''}
            error={errors.status}
            onChange={handleInputChange}
            onSave={null}
            originalValue={originalValues.status}
            isSaving={savingFields.status}
            isEditMode={false}
            disabled={isEditMode}
          />
        </Col>
      </Row>

      {/* Contact & Social Media Section */}
      <Row className="g-4 mt-1"> 
        <Col md={6}>
          <EditableField
            label="Work Email"
            name="workEmail"
            type="email"
            value={formData.workEmail || ''}
            error={errors.workEmail}
            onChange={handleInputChange}
            onSave={isEditMode ? handleFieldSave : null}
            originalValue={originalValues.workEmail}
            isSaving={savingFields.workEmail}
            isEditMode={isEditMode}
            placeholder="example@work.com"
          />
        </Col>
        <Col md={6}>
          <EditableField
            label="Personal Email"
            name="personalEmail"
            type="email"
            value={formData.personalEmail || ''}
            error={errors.personalEmail}
            onChange={handleInputChange}
            onSave={isEditMode ? handleFieldSave : null}
            originalValue={originalValues.personalEmail}
            isSaving={savingFields.personalEmail}
            isEditMode={isEditMode}
            placeholder="example@personal.com"
          />
        </Col>
      </Row>

      <Row className="g-4 mt-1">
        <Col md={6}>
          <EditableField
            label="Website"
            name="website"
            type="url"
            value={formData.website || ''}
            error={errors.website}
            onChange={handleInputChange}
            onSave={isEditMode ? handleFieldSave : null}
            originalValue={originalValues.website}
            isSaving={savingFields.website}
            isEditMode={isEditMode}
            placeholder="https://yourwebsite.com"
          />
        </Col>
        <Col md={6}>
          <EditableField
            label="LinkedIn"
            name="linkedin"
            type="text"
            value={formData.linkedin || ''}
            error={errors.linkedin}
            onChange={handleInputChange}
            onSave={isEditMode ? handleFieldSave : null}
            originalValue={originalValues.linkedin}
            isSaving={savingFields.linkedin}
            isEditMode={isEditMode}
            placeholder="linkedin.com/in/username"
          />
        </Col>
      </Row>

      <Row className="g-4 mt-1">
        <Col md={6}>
          <EditableField
            label="Twitter"
            name="twitter"
            type="text"
            value={formData.twitter || ''}
            error={errors.twitter}
            onChange={handleInputChange}
            onSave={isEditMode ? handleFieldSave : null}
            originalValue={originalValues.twitter}
            isSaving={savingFields.twitter}
            isEditMode={isEditMode}
            placeholder="@username"
          />
        </Col>
        <Col md={6}>
          <EditableField
            label="Facebook"
            name="facebook"
            type="text"
            value={formData.facebook || ''}
            error={errors.facebook}
            onChange={handleInputChange}
            onSave={isEditMode ? handleFieldSave : null}
            originalValue={originalValues.facebook}
            isSaving={savingFields.facebook}
            isEditMode={isEditMode}
            placeholder="facebook.com/username"
          />
        </Col>
      </Row>

      <hr />

      <DocumentUploadSection
        label="Identity Documents (Front & Back)"
        existingDocuments={isEditMode ? uploadedFiles.identity : []}
        onUpload={handleUpload}
        onRemove={isEditMode ? handleRemove : undefined}
        isEditMode={isEditMode}
        target="identity"
        options={identityOptions}
      />

      <DocumentUploadSection
        label="Work Authorization Documents (Front & Back)"
        existingDocuments={isEditMode ? uploadedFiles.workAuth : []}
        onUpload={handleUpload}
        onRemove={isEditMode ? handleRemove : undefined}
        isEditMode={isEditMode}
        target="workAuth"
        options={workOptions}
      />
    </>
  );
};

export default PersonalForm;
